import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageProvider";
import { MapPin } from "lucide-react";

const MAP_BBOX = "5.4,47.3,15.0,55.0"; // Germany bbox

export const Locations = () => {
  const { t } = useLang();
  // Build OSM marker URL for all cities
  const markers = t.locations.cities
    .map((c) => `&marker=${c.lat},${c.lng}`)
    .join("");
  const mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${MAP_BBOX}&layer=mapnik${markers}`;

  return (
    <section
      id="locations"
      data-testid="locations-section"
      className="relative scroll-mt-24 overflow-hidden bg-[#F8FAFC] py-24 dark:bg-[#0B1020] md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-[#1E3A8A]/15 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1E3A8A] dark:border-white/10 dark:bg-white/5 dark:text-[#A5B4FC]">
            {t.locations.eyebrow}
          </div>
          <h2 className="mt-5 font-display text-[clamp(2rem,4.2vw,3.25rem)] font-bold leading-tight tracking-tight text-[#0F172A] dark:text-white">
            {t.locations.title}
          </h2>
          <p className="mt-4 max-w-xl text-base text-slate-600 dark:text-slate-300 md:text-lg">
            {t.locations.subtitle}
          </p>
        </motion.div>

        <div className="mt-12 grid gap-6 lg:grid-cols-[1fr_1.2fr]">
          {/* Cities list */}
          <div className="grid gap-3 self-start">
            {t.locations.cities.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                data-testid="location-card"
                className="group flex items-center gap-5 rounded-3xl border border-slate-200/70 bg-white p-5 transition-all hover:border-[#7C3AED]/40 hover:shadow-elevated dark:border-white/5 dark:bg-[#0F172A]"
              >
                <div className="flex h-14 w-14 flex-none items-center justify-center rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#7C3AED] text-white shadow-glow-blue">
                  <MapPin className="h-7 w-7" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-xl font-bold tracking-tight text-[#0F172A] dark:text-white">
                    {c.name}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">{c.region}</div>
                </div>
                <div className="text-xs uppercase tracking-wider text-[#7C3AED]">DE</div>
              </motion.div>
            ))}
          </div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            data-testid="locations-map"
            className="relative overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-elevated dark:border-white/5 dark:bg-[#0F172A]"
          >
            <iframe
              title="Genius WorkPA Standorte"
              src={mapSrc}
              className="h-[480px] w-full"
              style={{ border: 0 }}
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/5 dark:ring-white/10" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Locations;
